<template>
    <v-dialog :model-value="modelValue" fullscreen scrollable @update:model-value="$emit('update:modelValue', $event)">
        <v-card v-if="dati">
            <v-toolbar density="compact" color="surface">
                <v-toolbar-title class="text-body-1">
                    Calcolo applicato secondo UNI EN ISO 10077-1 e UNI EN ISO 10077-2 —
                    Posizione {{ dati.rif }} <span class="text-medium-emphasis">{{ dati.codProgetto }}</span>
                    <span v-if="!dati.salvata" class="text-caption text-medium-emphasis ml-2">(non ancora salvata)</span>
                </v-toolbar-title>
                <v-btn icon @click="chiudi"><v-icon>mdi-close</v-icon></v-btn>
            </v-toolbar>

            <v-card-text class="pt-4">
                <v-row>
                    <!-- ===================== Colonna sinistra ===================== -->
                    <v-col cols="12" md="8">
                        <v-row dense>
                            <v-col cols="12" sm="5">
                                <div class="text-caption text-medium-emphasis mb-1">Misure serramento</div>
                                <div class="disegno-box">
                                    <DisegnoSerramentoSvg :forma="dati.forma" :l="dati.l" :h="dati.h"
                                        :corda="dati.corda" :battenti="battenti" :nr-riempimenti="dati.nrRiempimenti" />
                                </div>
                                <v-row dense class="mt-2">
                                    <v-col cols="6"><v-text-field :model-value="dati.nrRiempimenti" label="Riempimenti"
                                            readonly density="compact" hide-details /></v-col>
                                    <v-col cols="6"><v-text-field :model-value="battenti" label="N° battenti" readonly
                                            density="compact" hide-details /></v-col>
                                </v-row>
                                <div class="text-caption mt-2">
                                    Tipologia riempimenti: <strong>{{ nomeTipologia }}</strong> — Forma:
                                    <strong>{{ dati.forma }}</strong>
                                </div>
                            </v-col>

                            <v-col cols="12" sm="7">
                                <v-card variant="outlined" class="mb-3">
                                    <v-card-text>
                                        <div class="text-caption text-medium-emphasis mb-1">Telaio (mm)</div>
                                        <v-row dense>
                                            <v-col cols="6"><v-text-field :model-value="dati.l"
                                                    label="Larghezza (Lt)" readonly density="compact"
                                                    hide-details /></v-col>
                                            <v-col cols="6"><v-text-field :model-value="dati.h" label="Altezza (Ht)"
                                                    readonly density="compact" hide-details /></v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>

                                <v-card variant="outlined">
                                    <v-card-text>
                                        <div class="text-caption text-medium-emphasis mb-1">Riepilogo (m²)</div>
                                        <v-text-field :model-value="dati.areaTelaio" label="Aw — Area serramento (Lt·Ht)"
                                            readonly density="compact" class="mb-1" hide-details />
                                        <v-text-field v-model.number="dati.agSup" type="number" step="any"
                                            :label="`${sup.areaSigla} — Area ${sup.nome} (superiore)`" :disabled="!puoModificare"
                                            density="compact" class="mb-1" hide-details />
                                        <v-text-field v-model.number="dati.agInt" type="number" step="any"
                                            :label="`${inte.areaSigla} — Area ${inte.nome} (intermedio)`"
                                            :disabled="!puoModificare || dati.nrRiempimenti !== 3" density="compact"
                                            class="mb-1" hide-details />
                                        <v-text-field v-model.number="dati.apInf" type="number" step="any"
                                            :label="`${inf.areaSigla} — Area ${inf.nome} (inferiore)`"
                                            :disabled="!puoModificare || dati.nrRiempimenti < 2" density="compact"
                                            class="mb-1" hide-details />
                                        <v-text-field v-model.number="dati.lgSup" type="number" step="any"
                                            :label="`${sup.perSigla} — Perimetro ${sup.nome} (superiore)`"
                                            :disabled="!puoModificare" density="compact" class="mb-1" hide-details />
                                        <v-text-field v-model.number="dati.lgInt" type="number" step="any"
                                            :label="`${inte.perSigla} — Perimetro ${inte.nome} (intermedio)`"
                                            :disabled="!puoModificare || dati.nrRiempimenti !== 3" density="compact"
                                            class="mb-1" hide-details />
                                        <v-text-field v-model.number="dati.lpInf" type="number" step="any"
                                            :label="`${inf.perSigla} — Perimetro ${inf.nome} (inferiore)`"
                                            :disabled="!puoModificare || dati.nrRiempimenti < 2" density="compact"
                                            class="mb-1" hide-details />
                                        <v-text-field :model-value="dati.areaLegno" label="Af — Area legno (At−Av=Al)"
                                            readonly density="compact" hide-details />
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-card variant="outlined" class="mt-3">
                            <v-card-text>
                                <div class="text-caption text-medium-emphasis mb-1">Comune di installazione</div>
                                <v-row dense>
                                    <v-col cols="12" sm="6"><v-text-field :model-value="luogo" label="Comune del cantiere"
                                            readonly density="compact" hide-details /></v-col>
                                    <v-col cols="6" sm="3"><v-text-field :model-value="dati.zonaClimatica"
                                            label="Zona climatica" readonly density="compact" hide-details /></v-col>
                                    <v-col cols="6" sm="3"><v-text-field :model-value="dati.gradiGiorno"
                                            label="Gradi giorno" readonly density="compact" hide-details /></v-col>
                                </v-row>

                                <div class="text-caption text-medium-emphasis mt-3 mb-1">Valori limite</div>
                                <v-row v-if="dati.valoriLimite" dense>
                                    <v-col v-for="g in gruppiLimiti" :key="g.titolo" cols="12" sm="4">
                                        <div class="text-caption font-weight-medium">{{ g.titolo }}</div>
                                        <div class="d-flex" style="gap: 6px">
                                            <div v-for="v in g.voci" :key="v.anno" class="limite-box text-center"
                                                :class="classeEsito(v.esito)">
                                                <div class="text-caption">{{ v.anno }}</div>
                                                <div>{{ v.valore ?? '—' }}</div>
                                            </div>
                                        </div>
                                    </v-col>
                                </v-row>
                                <div v-else class="text-caption text-medium-emphasis">
                                    Nessun valore limite: scegli il comune del cantiere sulla commessa e carica i
                                    valori limite delle zone climatiche.
                                </div>
                            </v-card-text>
                        </v-card>

                        <v-row dense class="mt-3">
                            <v-col cols="12" sm="7">
                                <v-text-field v-model="dati.vetreria" label="Vetreria" :disabled="!puoModificare"
                                    density="compact" hide-details />
                            </v-col>
                            <v-col cols="12" sm="5">
                                <div class="d-flex align-center" style="gap: 8px">
                                    <v-text-field v-model="dati.rw" label="Rw (acustica)" :disabled="!puoModificare"
                                        density="compact" hide-details />
                                    <v-btn variant="text" size="small" color="primary" :loading="calcolandoRw"
                                        :disabled="!puoModificare" @click="calcolaRw">Calcola</v-btn>
                                </div>
                            </v-col>
                        </v-row>
                    </v-col>

                    <!-- ===================== Colonna destra ===================== -->
                    <v-col cols="12" md="4">
                        <v-card variant="outlined" class="mb-3">
                            <v-card-text>
                                <v-radio-group v-model="dati.calcoloUf" inline hide-details :disabled="!puoModificare"
                                    label="Legno" @update:model-value="cambiaMetodo">
                                    <v-radio label="10077-1" :value="1" />
                                    <v-radio label="10077-2" :value="2" />
                                </v-radio-group>
                                <v-text-field :model-value="dati.legno" label="Legno (articolo)" readonly
                                    density="compact" class="mt-2" hide-details />
                                <v-text-field v-model.number="dati.spessoreMedio" type="number" step="1"
                                    label="Sp. medio telaio (mm)" :disabled="!puoModificare || dati.calcoloUf !== 1"
                                    density="compact" class="mt-2" hide-details />
                                <v-select v-model="dati.tipoLegno" :items="tipiLegno" label="Tipo legno" clearable
                                    :disabled="!puoModificare" density="compact" class="mt-2" hide-details />
                                <div class="d-flex align-center mt-2" style="gap: 8px">
                                    <v-btn variant="text" size="small" color="primary" :disabled="!puoModificare"
                                        @click="ricalcolaUf()">Ricalcola</v-btn>
                                    <v-text-field v-model.number="dati.uf" type="number" step="any" label="Uf (W/m²K)"
                                        :disabled="!puoModificare" density="compact" hide-details />
                                    <v-btn variant="text" size="small" color="primary"
                                        :disabled="!puoModificare || dati.calcoloUf !== 2"
                                        @click="apriNodi">Nodi</v-btn>
                                </div>
                            </v-card-text>
                        </v-card>

                        <v-card variant="outlined" class="mb-3">
                            <v-card-text>
                                <div class="text-caption text-medium-emphasis mb-1">Riempimento</div>
                                <v-autocomplete v-model="dati.vetroSup" :items="riempimentiItems"
                                    item-title="label" item-value="codArticolo"
                                    label="Superiore" clearable density="compact" :disabled="!puoModificare"
                                    @update:model-value="cambiaRiempimento('sup', $event)" />
                                <v-text-field v-model.number="dati.ugSup" type="number" step="any"
                                    :label="`${sup.ugSigla} (W/m²K)`" :disabled="!puoModificare" density="compact"
                                    class="mb-2" hide-details />

                                <v-autocomplete v-model="dati.vetroInt" :items="riempimentiItems"
                                    item-title="label" item-value="codArticolo"
                                    label="Intermedio" clearable density="compact"
                                    :disabled="!puoModificare || dati.nrRiempimenti !== 3"
                                    @update:model-value="cambiaRiempimento('int', $event)" />
                                <v-text-field v-model.number="dati.ugInt" type="number" step="any"
                                    :label="`${inte.ugSigla} (W/m²K)`"
                                    :disabled="!puoModificare || dati.nrRiempimenti !== 3" density="compact"
                                    class="mb-2" hide-details />

                                <v-autocomplete v-model="dati.vetroInf" :items="riempimentiItems"
                                    item-title="label" item-value="codArticolo"
                                    label="Inferiore" clearable density="compact"
                                    :disabled="!puoModificare || dati.nrRiempimenti < 2"
                                    @update:model-value="cambiaRiempimento('inf', $event)" />
                                <v-text-field v-model.number="dati.ugInf" type="number" step="any"
                                    :label="`${inf.ugSigla} (W/m²K)`"
                                    :disabled="!puoModificare || dati.nrRiempimenti < 2" density="compact"
                                    hide-details />
                            </v-card-text>
                        </v-card>

                        <v-card variant="outlined" class="mb-3">
                            <v-card-text>
                                <div class="text-caption text-medium-emphasis mb-1">Giunzione</div>
                                <div class="d-flex align-center mb-2" style="gap: 8px">
                                    <v-text-field :model-value="dati.distSup" label="Sup." readonly density="compact"
                                        style="max-width: 90px" hide-details />
                                    <v-text-field v-model.number="dati.psiSup" type="number" step="any"
                                        :label="`${sup.psiSigla} (W/mK)`" :disabled="!puoModificare" density="compact"
                                        hide-details />
                                </div>
                                <div class="d-flex align-center mb-2" style="gap: 8px">
                                    <v-text-field :model-value="dati.distInt" label="Int." readonly density="compact"
                                        style="max-width: 90px" hide-details />
                                    <v-text-field v-model.number="dati.psiInt" type="number" step="any"
                                        :label="`${inte.psiSigla} (W/mK)`"
                                        :disabled="!puoModificare || dati.nrRiempimenti !== 3" density="compact"
                                        hide-details />
                                </div>
                                <div class="d-flex align-center" style="gap: 8px">
                                    <v-text-field :model-value="dati.distInf" label="Inf." readonly density="compact"
                                        style="max-width: 90px" hide-details />
                                    <v-text-field v-model.number="dati.psiInf" type="number" step="any"
                                        :label="`${inf.psiSigla} (W/mK)`"
                                        :disabled="!puoModificare || dati.nrRiempimenti < 2" density="compact"
                                        hide-details />
                                </div>
                            </v-card-text>
                        </v-card>

                        <v-card variant="outlined" color="success" class="formula-box">
                            <v-card-text>
                                <div class="text-caption mb-1">Formula</div>
                                <div class="formula text-center mb-3">
                                    <div>Ag·Ug + Af·Uf + Lg·Ψg</div>
                                    <div class="linea"></div>
                                    <div>Aw</div>
                                </div>
                                <div class="d-flex align-center" style="gap: 8px">
                                    <v-text-field v-model.number="dati.uw" type="number" step="any"
                                        label="Uw (W/m²K)" :disabled="!puoModificare" density="compact"
                                        class="uw-campo" hide-details @update:model-value="uwManuale = true" />
                                    <v-btn size="small" :loading="calcolando" :disabled="!puoModificare"
                                        @click="calcola">Calcola</v-btn>
                                </div>
                                <div v-if="daRicalcolare" class="text-caption text-warning font-weight-bold mt-1">
                                    Valori modificati dopo l'ultimo calcolo: Af e Uw non sono aggiornati, premi Calcola.
                                </div>
                                <div class="text-caption text-error mt-1">
                                    NOTA: verificare i valori limite prima di proseguire
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <v-alert v-if="avvisiNodi.length" type="warning" variant="tonal" density="compact" class="mt-3"
                    title="Nodi del telaio">
                    <ul class="pl-4">
                        <li v-for="(a, i) in avvisiNodi" :key="i">{{ a }}</li>
                    </ul>
                </v-alert>
                <v-alert v-if="dati.avvisi && dati.avvisi.length" type="warning" variant="tonal" density="compact"
                    class="mt-3" title="Avvisi">
                    <ul class="pl-4">
                        <li v-for="(a, i) in dati.avvisi" :key="i">{{ a }}</li>
                    </ul>
                </v-alert>
            </v-card-text>

            <v-divider />
            <v-card-actions>
                <v-btn variant="text" prepend-icon="mdi-clipboard-text-outline" @click="dialogPrestazioni = true">
                    Prestazioni</v-btn>
                <v-spacer />
                <v-btn variant="text" @click="chiudi">{{ puoModificare ? 'Annulla' : 'Chiudi' }}</v-btn>
                <v-btn v-if="puoModificare" color="primary" :loading="salvando" @click="salva">Salva</v-btn>
            </v-card-actions>
        </v-card>
        <v-card v-else>
            <v-card-text class="d-flex justify-center pa-8"><v-progress-circular indeterminate /></v-card-text>
        </v-card>

        <!-- Valori prestazioni (Rw, aria, acqua, vento, urto...) -->
        <PrestazioniPosizione v-model="dialogPrestazioni" :commessa-id="commessaId" :posizione="posizione"
            :puo-modificare="puoModificare" :riempimenti="riempimentiCorrenti" />

        <!-- ===================== Dialog nodi (10077-2) ===================== -->
        <v-dialog v-model="dialogNodi" max-width="900">
            <v-card>
                <v-card-title>Nodi del telaio (metodo ai nodi UNI EN ISO 10077-2)</v-card-title>
                <v-card-text>
                    <v-row dense class="align-center">
                        <v-col cols="12" sm="4">
                            <v-select v-model="tipologiaNodi" :items="tipologieNodi" label="Tipologia nodi"
                                density="compact" hide-details @update:model-value="caricaNodiDisponibili" />
                        </v-col>
                        <v-col cols="12" sm="8" class="text-caption text-medium-emphasis">
                            Ricerca nell'anagrafica nodi: legno <strong>{{ dati?.tipoLegno || '—' }}</strong>, sezione
                            <strong>{{ dati?.spessorePosizione }}</strong>, vano vetro
                            <strong>{{ dati?.vanoVetro ?? 0 }}</strong>.
                        </v-col>
                    </v-row>

                    <v-row dense class="align-center mt-2">
                        <v-col cols="12" sm="6">
                            <v-select v-model="nuovoNodo.codNodo" :items="nodiDisponibili" item-value="codNodo"
                                :item-title="etichettaNodo" label="Nodo da aggiungere" density="compact" hide-details
                                no-data-text="Nessun nodo nell'anagrafica per questa combinazione" />
                        </v-col>
                        <v-col cols="6" sm="2">
                            <v-text-field v-model.number="nuovoNodo.lunghezza" type="number" label="Lunghezza (mm)"
                                density="compact" hide-details @update:model-value="calcolaAreaNuovoNodo" />
                        </v-col>
                        <v-col cols="6" sm="2">
                            <v-text-field v-model.number="nuovoNodo.area" type="number" label="Area (mm²)"
                                density="compact" hide-details />
                        </v-col>
                        <v-col cols="12" sm="2">
                            <v-btn color="primary" variant="tonal" block :disabled="!nuovoNodo.codNodo"
                                @click="aggiungiNodo">Aggiungi</v-btn>
                        </v-col>
                    </v-row>

                    <v-table density="compact" class="mt-3">
                        <thead>
                            <tr>
                                <th>Cod.</th>
                                <th>Descrizione</th>
                                <th>Area (mm²)</th>
                                <th>Uw nodo</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(n, i) in nodiLocali" :key="i">
                                <td>{{ n.codNodo }}</td>
                                <td>{{ n.descrizione }}</td>
                                <td style="width: 140px"><v-text-field v-model.number="n.area" type="number"
                                        density="compact" hide-details variant="underlined" /></td>
                                <td style="width: 120px"><v-text-field v-model.number="n.uw" type="number" step="any"
                                        density="compact" hide-details variant="underlined" /></td>
                                <td class="text-right">
                                    <v-btn icon size="x-small" variant="text" color="error"
                                        @click="nodiLocali.splice(i, 1)"><v-icon size="18">mdi-delete</v-icon></v-btn>
                                </td>
                            </tr>
                            <tr v-if="nodiLocali.length === 0">
                                <td colspan="5" class="text-center text-medium-emphasis">Nessun nodo inserito</td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" @click="dialogNodi = false">Annulla</v-btn>
                    <v-btn color="primary" @click="confermaNodi">Ok</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import PrestazioniPosizione from "@/components/commesse/PrestazioniPosizione.vue";
import DisegnoSerramentoSvg from "@/components/commesse/DisegnoSerramentoSvg.vue";
import {
    get_trasmittanza,
    calcola_trasmittanza,
    uf_trasmittanza,
    salva_trasmittanza,
    nodi_disponibili,
    info_riempimento,
    calcola_prestazioni,
} from "@/api/api.trasmittanza.js";

const CAMPI_NUMERICI = [
    "l", "h", "spessoreMedio", "uf",
    "ugSup", "ugInt", "ugInf", "agSup", "lgSup", "agInt", "lgInt", "apInf", "lpInf",
    "psiSup", "psiInt", "psiInf", "areaTelaio", "areaLegno", "uw",
];

export default {
    name: "TrasmittanzaPosizione",
    components: { PrestazioniPosizione, DisegnoSerramentoSvg },

    props: {
        modelValue: { type: Boolean, default: false },
        commessaId: { type: [String, Number], required: true },
        posizione: { type: Object, default: null },
        puoModificare: { type: Boolean, default: false },
        articoli: { type: Array, default: () => [] },
        luogo: { type: String, default: "" },
    },
    emits: ["update:modelValue", "salvato"],

    data() {
        return {
            dati: null,
            calcolando: false,
            salvando: false,
            tipiLegno: ["SuperTenero", "Tenero", "Duro", "ExtraDuro"],
            calcolandoRw: false,
            // Avvisi dell'ultima costruzione automatica dei nodi (nodo mancante in anagrafica, tipo legno...).
            avvisiNodi: [],
            // Impronta dei valori da cui dipendono Af e Uw al momento dell'ultimo calcolo/caricamento,
            // e Uw modificato a mano dall'utente (resta quello digitato finché non si preme Calcola).
            chiaveUltimoCalcolo: "",
            uwManuale: false,
            dialogPrestazioni: false,
            dialogNodi: false,
            tipologieNodi: ["SerramentiLegno", "AlzantiLegno", "AlzantiAlluminio", "SerramentiAlluminio"],
            tipologiaNodi: "SerramentiLegno",
            nodiDisponibili: [],
            nodiLocali: [],
            nuovoNodo: { codNodo: null, lunghezza: null, area: null },
        };
    },

    computed: {
        riempimentiItems() {
            return this.articoli
                .filter(a => a.categoria === "VETRO" || a.categoria === "PANNELLO")
                .map(a => ({ ...a, label: `${a.codArticolo}${a.descrizione ? " — " + a.descrizione : ""}` }));
        },
        // Riempimenti scelti nella videata (anche se non ancora salvati): base del calcolo delle prestazioni.
        riempimentiCorrenti() {
            if (!this.dati) return null;
            return {
                nrRiempimenti: this.dati.nrRiempimenti,
                vetroSup: this.dati.vetroSup,
                vetroInt: this.dati.vetroInt,
                vetroInf: this.dati.vetroInf,
            };
        },
        chiaveCalcolo() {
            const d = this.dati;
            if (!d) return "";
            return JSON.stringify([
                d.agSup, d.lgSup, d.agInt, d.lgInt, d.apInf, d.lpInf,
                d.ugSup, d.ugInt, d.ugInf, d.uf, d.psiSup, d.psiInt, d.psiInf,
            ].map(v => Number(v) || 0));
        },
        daRicalcolare() {
            return !!this.dati && this.chiaveCalcolo !== this.chiaveUltimoCalcolo;
        },
        battenti() {
            return this.posizione ? this.posizione.battenti : "";
        },
        nomeTipologia() {
            return { T: "trasparente", O: "opaca", TO: "mista trasparente/opaca", NN: "non gestita" }[this.dati?.tipologia] || "—";
        },
        // Etichette del VB: Ag/Ug/Ψg e Lg per i vetri, Ap/Up/Ψp e Lp per i pannelli.
        sup() { return this.sigle(this.dati?.supVetro); },
        inte() { return this.sigle(this.dati?.intVetro); },
        inf() { return this.sigle(this.dati?.infVetro); },
        gruppiLimiti() {
            const l = this.dati?.valoriLimite;
            const e = this.dati?.limiti || {};
            if (!l) return [];
            return [
                {
                    titolo: "Serramenti (Uw)",
                    voci: [
                        { anno: 2006, valore: l.serramento2006, esito: e.serramento2006 },
                        { anno: 2008, valore: l.serramento2008, esito: e.serramento2008 },
                        { anno: 2010, valore: l.serramento2010, esito: e.serramento2010 },
                    ],
                },
                {
                    titolo: "Vetri (Ug)",
                    voci: [
                        { anno: 2006, valore: l.vetro2006, esito: e.vetro2006 },
                        { anno: 2008, valore: l.vetro2008, esito: e.vetro2008 },
                        { anno: 2011, valore: l.vetro2011, esito: e.vetro2011 },
                    ],
                },
                {
                    titolo: "Detrazione 55% (Uw)",
                    voci: [
                        { anno: 2009, valore: l.detrazione2009, esito: e.detrazione2009 },
                        { anno: 2010, valore: l.detrazione2010, esito: e.detrazione2010 },
                    ],
                },
            ];
        },
    },

    watch: {
        modelValue(aperto) {
            if (aperto) this.carica();
        },
    },

    methods: {
        sigle(vetro) {
            return vetro === false
                ? { areaSigla: "Ap", perSigla: "Lp", ugSigla: "Up", psiSigla: "Ψp", nome: "pannelli" }
                : { areaSigla: "Ag", perSigla: "Lg", ugSigla: "Ug", psiSigla: "Ψg", nome: "vetri" };
        },

        classeEsito(esito) {
            if (esito === true) return "esito-ok";
            if (esito === false) return "esito-ko";
            return "";
        },

        async carica() {
            this.dati = null;
            this.avvisiNodi = [];
            if (!this.posizione) return;
            try {
                const resp = await get_trasmittanza(this.commessaId, this.posizione.id);
                this.applica(resp.data);
                this.tipologiaNodi = this.dati.tipologiaNodi || "SerramentiLegno";
            } catch (error) {
                console.log(error);
                this.$emit("update:modelValue", false);
            }
        },

        // Il form usa v-model.number: i campi svuotati restano "" e vanno resi numeri per il backend.
        payload() {
            const d = { ...this.dati };
            for (const k of CAMPI_NUMERICI) d[k] = Number(d[k]) || 0;
            d.spessoreMedio = Math.round(d.spessoreMedio);
            d.nodi = (this.dati.nodi || []).map(n => ({ ...n, area: Number(n.area) || 0, uw: Number(n.uw) || 0 }));
            return d;
        },

        // Il server restituisce l'intero oggetto aggiornato (Aw, Af, Uw, avvisi, limiti).
        applica(risultato) {
            this.dati = risultato;
            this.chiaveUltimoCalcolo = this.chiaveCalcolo;
            this.uwManuale = false;
        },

        async calcola() {
            this.calcolando = true;
            try {
                const resp = await calcola_trasmittanza(this.commessaId, this.posizione.id, this.payload());
                this.applica(resp.data);
            } catch (error) {
                console.log(error);
            }
            this.calcolando = false;
        },

        // Passando al 10077-1 si propone lo spessore dell'anta come spessore medio del telaio.
        async cambiaMetodo(metodo) {
            if (metodo === 1 && !this.dati.spessoreMedio) this.dati.spessoreMedio = this.dati.spessorePosizione || 0;
            await this.ricalcolaUf();
        },

        // Rw della videata = acustica dichiarata (getAcustica del VB) per i riempimenti scelti.
        async calcolaRw() {
            this.calcolandoRw = true;
            try {
                const resp = await calcola_prestazioni(this.commessaId, this.posizione.id, this.riempimentiCorrenti);
                this.dati.rw = resp.data.acustica;
            } catch (error) {
                console.log(error);
            }
            this.calcolandoRw = false;
        },

        // "Ricalcola": 10077-1 da anagrafica Uf legno; 10077-2 ricostruisce i nodi dall'anagrafica nodi
        // (come il VB) e ne ricava Uf. Dopo la modifica manuale dei nodi si usa ricostruisci = false.
        async ricalcolaUf(ricostruisci = true) {
            try {
                const resp = await uf_trasmittanza(this.commessaId, this.posizione.id, this.payload(), ricostruisci);
                this.dati.uf = resp.data.uf;
                if (this.dati.calcoloUf === 2) this.dati.nodi = resp.data.nodi;
                this.avvisiNodi = resp.data.avvisi || [];
            } catch (error) {
                console.log(error);
            }
        },

        // Alla scelta di un articolo si leggono Ug/Up e giunzione dall'anagrafica (getUgVetro/getDist del VB).
        async cambiaRiempimento(posizione, codArticolo) {
            const campi = {
                sup: { ug: "ugSup", dist: "distSup", psi: "psiSup", vetro: "supVetro" },
                int: { ug: "ugInt", dist: "distInt", psi: "psiInt", vetro: "intVetro" },
                inf: { ug: "ugInf", dist: "distInf", psi: "psiInf", vetro: "infVetro" },
            }[posizione];

            try {
                const resp = await info_riempimento(codArticolo || "");
                const info = resp.data;
                this.dati[campi.ug] = info.ug;
                this.dati[campi.dist] = info.distCodice;
                this.dati[campi.psi] = info.psi;
                this.dati[campi.vetro] = info.categoria !== "PANNELLO";
            } catch (error) {
                console.log(error);
            }
        },

        // ---- Nodi ----
        async apriNodi() {
            this.nodiLocali = (this.dati.nodi || []).map(n => ({ ...n }));
            this.nuovoNodo = { codNodo: null, lunghezza: null, area: null };
            this.dialogNodi = true;
            await this.caricaNodiDisponibili();
        },

        async caricaNodiDisponibili() {
            try {
                const resp = await nodi_disponibili({
                    legno: this.dati.tipoLegno,
                    sezione: this.dati.spessorePosizione,
                    vanoVetro: this.dati.vanoVetro || 0,
                    tipologia: this.tipologiaNodi,
                });
                this.nodiDisponibili = resp.data;
            } catch (error) {
                this.nodiDisponibili = [];
                console.log(error);
            }
        },

        etichettaNodo(n) {
            return `${n.codNodo} — ${n.descrizione || ""} (L ${n.lNodo}, Uw ${n.uw})`;
        },

        // Area del nodo = larghezza del nodo (anagrafica) per la lunghezza indicata.
        calcolaAreaNuovoNodo() {
            const nodo = this.nodiDisponibili.find(n => n.codNodo === this.nuovoNodo.codNodo);
            if (nodo && this.nuovoNodo.lunghezza) this.nuovoNodo.area = Math.round(nodo.lNodo * this.nuovoNodo.lunghezza);
        },

        aggiungiNodo() {
            const nodo = this.nodiDisponibili.find(n => n.codNodo === this.nuovoNodo.codNodo);
            if (!nodo) return;
            this.nodiLocali.push({
                codNodo: nodo.codNodo,
                descrizione: nodo.descrizione,
                area: Number(this.nuovoNodo.area) || 0,
                uw: nodo.uw,
                tipo: this.tipologiaNodi,
            });
            this.nuovoNodo = { codNodo: null, lunghezza: null, area: null };
        },

        // Come in Frm_dettaglioNodi: alla conferma Uf viene ricalcolato dai nodi (CalcolaUf).
        async confermaNodi() {
            this.dati.nodi = this.nodiLocali.map(n => ({ ...n }));
            this.dialogNodi = false;
            await this.ricalcolaUf(false);
        },

        async salva() {
            this.salvando = true;
            try {
                // Aw e Af sono sempre ricalcolati dal server: se i dati sono cambiati senza premere Calcola
                // si ricalcola prima, altrimenti si salverebbe un Uw non coerente con Af (salvo Uw digitato a mano).
                if (this.daRicalcolare && !this.uwManuale) await this.calcola();
                const resp = await salva_trasmittanza(this.commessaId, this.posizione.id, this.payload());
                this.applica(resp.data);
                this.$emit("salvato");
                this.$emit("update:modelValue", false);
            } catch (error) {
                console.log(error);
            }
            this.salvando = false;
        },

        chiudi() {
            this.$emit("update:modelValue", false);
        },
    },
};
</script>

<style scoped>
.disegno-box {
    width: 100%;
    height: 230px;
    background: rgba(0, 0, 0, 0.04);
    border: 1px dashed rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    padding: 8px;
}

.limite-box {
    min-width: 54px;
    padding: 4px 6px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 4px;
}

.esito-ok {
    background: #c8e6c9;
}

.esito-ko {
    background: #ffcdd2;
}

.formula .linea {
    border-top: 1px solid currentColor;
    margin: 2px 24px;
}

.uw-campo {
    font-weight: 700;
}
</style>
